'use client';

import { Avatar, Box, Container, Grid, Typography, useTheme } from '@mui/material';
import React from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { ComponentBlocksPageSeparator } from '@/types';

interface PageSeparatorBlockProps {
  block: ComponentBlocksPageSeparator;
}

export default function PageSeparatorBlock({ block }: PageSeparatorBlockProps) {
  const theme = useTheme();
  const {
    SeparatorTitle,
    SeparatorDescription,
    SeparatorBlurb,
    UpdatedDate,
    WrittenBy,
    ReviewedBy,
  } = block;

  const LEFT_OFFSET = `calc(48px + ${theme.spacing(2)})`;
  const backgroundColor = block.PageSeparatorBackground?.BackgroundColor ?? 'secondary';

  return (
    <>
      <CustomBackground
        displayOptions={
          {
            ...block.PageSeparatorDisplayOptions,
            PaddingTop: block.PageSeparatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block.PageSeparatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block.PageSeparatorDisplayOptions?.PaddingInline ?? 'xs',
          } as StrapiDisplayOptions
        }
        backgroundOptions={
          {
            ...(block.PageSeparatorBackground || {}),
            BackgroundColor: backgroundColor,
          } as StrapiBackgroundOptions
        }
      >
        <Box sx={{ color: 'inherit', px: 3, borderRadius: 1 }}>
          <Container maxWidth="xl" sx={{ maxWidth: '100%' }}>
            <Box
              sx={
                backgroundColor === 'secondary'
                  ? {
                      '*': {
                        // add a text shadow to make the minimum accessible contrast ratio against the background
                        textShadow: `0 0 0.5rem ${theme.palette.purple[800]}`,
                      },
                    }
                  : {}
              }
            >
              <Typography variant="h2" fontWeight="bold" gutterBottom>
                {SeparatorTitle}
              </Typography>

              {SeparatorDescription && !SeparatorBlurb && (
                <Typography variant="body1" color="inherit" fontSize={18} mb={4}>
                  {SeparatorDescription}
                </Typography>
              )}

              {SeparatorBlurb && <GlobalBlockRenderer content={SeparatorBlurb || []} />}

              {UpdatedDate && (
                <Typography
                  variant="body1"
                  fontSize={18}
                  sx={{ color: 'rgba(255,255,255,0.75)', mt: -2 }}
                >
                  Updated: {new Date(UpdatedDate).toLocaleDateString('en-AU')}
                </Typography>
              )}
            </Box>
          </Container>
        </Box>
      </CustomBackground>

      {(WrittenBy || ReviewedBy) && (
        <Box
          sx={{
            background: theme.palette.grey[300],
            py: 2,
            px: 3,
            borderRadius: 1,
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ pl: { xs: 0, sm: LEFT_OFFSET } }}>
              <Grid container spacing={4} alignItems="center">
                {WrittenBy && (
                  <Grid item>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item>
                        <Avatar
                          src={
                            getImageUrl({
                              image: WrittenBy?.Avatar,
                              size: 'small',
                            }) ?? undefined
                          }
                          alt={WrittenBy?.Name || 'Finance Specialist'}
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="caption">
                          Written by{' '}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ cursor: 'pointer', fontWeight: 600 }}
                          >
                            {WrittenBy.Name}
                          </Typography>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {ReviewedBy && (
                  <Grid item>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item>
                        <Avatar
                          src={
                            getImageUrl({
                              image: ReviewedBy.Avatar,
                              size: 'small',
                            }) ?? undefined
                          }
                          alt={ReviewedBy.Name || 'Finance Specialist'}
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="caption">
                          Reviewed by{' '}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ cursor: 'pointer', fontWeight: 600 }}
                          >
                            {ReviewedBy.Name}
                          </Typography>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Container>
        </Box>
      )}
    </>
  );
}
